import { useMemo } from 'react';
import { IBuilding } from 'spw-redux/modules/projects/types';
import { useSelector } from 'react-redux';
import {
  currentProject,
  selectTotalBuildingAndFloorCount,
} from 'spw-redux/modules/projects/selectors';
import {
  selectSelectedLevels,
  selectSelectedLevelsExpanded,
} from 'spw-redux/modules/program-summary/esa/selectors';

const findFloorAndBuilding = (floorId: number, buildings: IBuilding[]) => {
  for (let i = 0; i < buildings.length; i++) {
    const building = buildings[i];

    for (let j = 0; j < building.floors.length; j++) {
      const floor = building.floors[j];

      if (floor.id === floorId) return { building, floor };
    }
  }

  return undefined;
};

export const useESASelectedFloorAndBuildingLabels = (): {
  addressLabel: string;
  floorLabel: string;
} => {
  const project = useSelector(currentProject);
  const {
    buildingIds: buildingIdsWithAllFloorsSelected,
    floorIds: remainingSelectedFloorIds,
  } = useSelector(selectSelectedLevels);
  const { allSelectedFloorIds, allSelectedBuildingIds } = useSelector(
    selectSelectedLevelsExpanded
  );
  const [totalBuildingCount, totalFloorCount] = useSelector(
    selectTotalBuildingAndFloorCount
  );

  return useMemo(() => {
    const { buildings: buildingsInProject } = project;

    // A single floor
    if (allSelectedFloorIds.length === 1) {
      const { building, floor } = findFloorAndBuilding(
        allSelectedFloorIds[0],
        buildingsInProject
      );

      return {
        addressLabel: building.streetAddress,
        floorLabel: floor.name,
      };
    }

    // A single building with all it's floors
    if (
      buildingIdsWithAllFloorsSelected.length === 1 &&
      !remainingSelectedFloorIds.length
    ) {
      return {
        addressLabel: buildingsInProject.find(
          (b) => b.id === buildingIdsWithAllFloorsSelected[0]
        )?.streetAddress,
        floorLabel:
          buildingsInProject.length === 1 ? 'All Floors' : 'Multiple Floors',
      };
    }

    const totalBuildingsSelected = buildingsInProject.filter((b) =>
      allSelectedBuildingIds.includes(b.id)
    );

    // A Single building
    if (totalBuildingsSelected.length === 1) {
      return {
        addressLabel: totalBuildingsSelected[0].streetAddress,
        floorLabel: 'Multiple Floors',
      };
    }

    // All buildings
    if (totalBuildingsSelected.length === totalBuildingCount) {
      return {
        addressLabel: 'All Buildings',
        floorLabel:
          allSelectedFloorIds.length === totalFloorCount
            ? 'All Floors'
            : 'Multiple Floors',
      };
    }

    // Multiple buildings
    return {
      addressLabel: 'Multiple Buildings',
      floorLabel: 'Multiple Floors',
    };
  }, [
    project,
    buildingIdsWithAllFloorsSelected,
    remainingSelectedFloorIds,
    allSelectedFloorIds,
    allSelectedBuildingIds,
    totalBuildingCount,
    totalFloorCount,
  ]);
};
